import { AsyncPipe } from '@angular/common';
import { Component, inject } from '@angular/core';
import { Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { PrivaButtonComponent } from '@priva/components/button';
import { PrivaFormGroupComponent } from '@priva/components/form-group';
import { PrivaNotificationComponent } from '@priva/components/notification';
import { PrivaLocalizationModule } from '@priva/localization';
import { AppAreaService, AppAreaType } from '@priva/utilities/layout';

import { absoluteRoutePaths } from 'app/core/routing';
import { selectAppMode } from 'app/features/settings/state/settings/settings.selectors';
import { userLogout } from 'app/state/user-session/user-session.actions';
import {
    selectIsAuthenticated,
    selectUserSession,
    selectUserSessionEmployee,
} from 'app/state/user-session/user-session.selectors';

@Component({
    selector: 'app-user-session-info',
    templateUrl: './user-session-info.component.html',
    styleUrl: './user-session-info.component.scss',
    imports: [
        AsyncPipe,
        PrivaButtonComponent,
        PrivaFormGroupComponent,
        PrivaLocalizationModule,
        PrivaNotificationComponent,
    ],
})
export class UserSessionInfoComponent {
    private store = inject(Store);
    private router = inject(Router);
    private appAreaService = inject(AppAreaService);

    loginTime$: Observable<string> = this.store.pipe(
        select(selectUserSession),
        map((userSession) => {
            return userSession ? new Date(userSession.startedOn).toLocaleString() : '';
        }),
    );

    userName$: Observable<string> = combineLatest([
        this.store.select(selectUserSessionEmployee),
        this.store.select(selectAppMode),
    ]).pipe(
        map(([employee, appMode]) => {
            if (!employee || appMode === 'multi-user') {
                return '';
            }

            const employeeNumberString = employee.employeeNumber.toString();
            return employee.employee?.name
                ? employee.employee.name + ' - ' + employeeNumberString
                : employeeNumberString;
        }),
    );

    isPersonalAppMode$ = this.store.select(selectAppMode).pipe(map((appMode) => appMode === 'personal'));

    isAuthenticated$ = this.store.select(selectIsAuthenticated);

    logout() {
        this.closePanel();
        this.store.dispatch(userLogout());
        this.router.navigateByUrl(absoluteRoutePaths.signin).then();
    }

    login() {
        this.closePanel();
        this.router.navigateByUrl(absoluteRoutePaths.signin).then();
    }

    private closePanel() {
        this.appAreaService.setAreaAsHidden(AppAreaType.AccountSidebar);
    }
}
